import { Add, Delete } from '@mui/icons-material';
import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Contact } from '../../../types/Contact';
import { PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';
import AddContactModal from './AddContactModal';
import PreferencesPopover from '../../common/popover/PreferencesPopover';

interface IContactTableProps {
  accountUuid: string;
  contacts: Contact[];
  setContacts: (contacts: Contact[]) => void;
}

export default function ContactTable({ accountUuid, contacts, setContacts }: IContactTableProps) {
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { disabled } = useContext(DisableContext);

  const onAdd = (addedContacts: Contact[]) => {
    const netNewContacts = _.differenceBy(addedContacts, contacts, 'uuid');
    setContacts([...contacts, ...netNewContacts]);
  };

  const handleRemove = () => {
    setContacts(_.reject(contacts, (contact) => _.includes(selectedContacts, contact.uuid)));
    setSelectedContacts([]);
  };

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE] },
    },
  });

  const prefByUuid = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const contactTableRows = contacts.map((contact: Contact) => {
    const handleSelectionChange = (checked: boolean) => {
      const newSelections = checked ? [...selectedContacts, contact.uuid] : _.reject(selectedContacts, (selectedUuid: string) => selectedUuid === contact.uuid);
      setSelectedContacts(newSelections);
    };

    return (
      <TableRow key={contact.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={_.includes(selectedContacts, contact.uuid)}
            onChange={(event) => handleSelectionChange(event.target.checked)}
          />
        </TableCell>
        <TableCell>
          <DrawerLink module={UserConfigurableModule.CONTACTS} uuid={contact.uuid}>
            {contact.full_name}
          </DrawerLink>
        </TableCell>
        <TableCell>
          <PreferencesPopover values={_.filter(_.map(contact.roles_uuids, uuid => prefByUuid[uuid]))} />
        </TableCell>
        <TableCell>{contact.email}</TableCell>
        <TableCell>{contact.phone_1}</TableCell>
      </TableRow>
    );
  });

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
      {contacts.length > 0 && (
        <ResponsiveButton startIcon={<Delete />} variant="outlined" color="warning" onClick={handleRemove} disabled={disabled}>
          Remove
        </ResponsiveButton>
      )}

      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  return (
    <AccordionSection
      title="Contacts"
      headerElements={actionButtons}
      collapseId={UserSettingsCollapseKeys.EVENT_ITEM_CONTACTS}
      hasData={contacts.length > 0}
      scrollable
    >
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{contactTableRows}</TableBody>
        </Table>
      </TableContainer>

      <AddContactModal
        accountUuid={accountUuid}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        alreadyAddedContacts={_.map(contacts, 'uuid')}
        onAdd={onAdd}
      />
    </AccordionSection>
  );
}
